import { useUploadFile } from '@core/shared';
import { OptionsApi, PollVoteWeights } from '@core/swagger-client';
import {
    Button,
    Close,
    CompositePageHeader,
    ContainerNoTopSpace,
    ContainerWebinarInner,
    ContentHeader,
    ErrorMessage,
    ErrorMessagePage,
    Heading,
    Input,
    LoaderPage,
    PageHeader,
    PageHeaderContent,
    Upload,
    useFileDropzone
} from '@core/ui';
import { getRouteApi } from '@online-webinar/router';
import { BackLink } from '@online-webinar/ui-frontend';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

const route = getRouteApi('/$webinarId/polls/$pollId/weighting');
export const PollWeighting = () => {
    const { webinarId, pollId } = route.useParams();
    const { upload, progress, isSuccess } = useUploadFile(`/api/poll/${webinarId}/${pollId}/import-weights`);
    const { data: webinar, error: webinarError, isLoading: webinarLoading } = useQuery(OptionsApi.webinar.get(webinarId!));
    const {
        data: pollWeightsData,
        error: pollWeightsError,
        isLoading: pollWeightsLoading,
        refetch: refetchPollWeights
    } = useQuery(OptionsApi.poll.getVoteWeights({ webinarId, pollId }));
    const [pollWeights, setPollWeights] = useState<PollVoteWeights>({ defaultWeight: 1, weights: [] });
    const [defaultWeight, setDefaultWeight] = useState<number>(1);

    const { getRootProps, getInputProps, isDragActive, rejectionCode } = useFileDropzone({
        multiple: false,
        onDrop: (acceptedFiles) => {
            if (acceptedFiles.length === 0) {
                return;
            }

            const filePayload = new FormData();
            filePayload.append('file', acceptedFiles[0]);
            upload(filePayload).then((data) => {
                refetchPollWeights();
            });
        },
        accept: {
            'application/vnd.ms-excel': ['.xls', '.xlsx', '.csv']
        }
    });

    useEffect(() => {
        if (pollWeightsData) {
            setPollWeights(pollWeightsData);
        }
    }, [pollWeightsData]);

    useEffect(() => {
        if (pollWeights) {
            setDefaultWeight(pollWeights.defaultWeight);
        }
    }, [pollWeights]);

    const setDefaultVoteWeightMutation = useMutation(OptionsApi.poll.setDefaultVoteWeight());
    useEffect(() => {
        if (pollWeightsLoading) {
            return;
        }
        setDefaultVoteWeightMutation.mutate({ webinarId, pollId, weight: defaultWeight });
    }, [defaultWeight]);

    const removeVoteWeightMutation = useMutation(OptionsApi.poll.removeVoteWeight());
    const removeVoteWeight = (emailAddress: string) => {
        removeVoteWeightMutation.mutate(
            { webinarId, pollId, emailAddress },
            {
                onSuccess: (data) => setPollWeights(data || { defaultWeight: 1, weights: [] })
            }
        );
    };

    const clearVoteWeightsMutation = useMutation(OptionsApi.poll.clearVoteWeights());
    const clearVoteWeights = () => {
        clearVoteWeightsMutation.mutate(
            { webinarId, pollId },
            {
                onSuccess: (data) => setPollWeights(data || { defaultWeight: 1, weights: [] })
            }
        );
    };

    const { t } = useTranslation();

    if (webinarLoading || !webinar || pollWeightsLoading || !pollWeightsData) {
        return <LoaderPage />;
    }

    const error = webinarError ?? pollWeightsError;
    if (error) {
        return <ErrorMessagePage apiError={error} />;
    }

    const isPending = setDefaultVoteWeightMutation.isPending || removeVoteWeightMutation.isPending || clearVoteWeightsMutation.isPending;
    return (
        <ContainerNoTopSpace>
            <PageHeader>
                <PageHeaderContent>{webinar && <CompositePageHeader title={webinar.title} broadcastDateTime={webinar.broadcastDateTime} />}</PageHeaderContent>
            </PageHeader>
            <ContainerWebinarInner>
                <BackLink to="/$webinarId/polls" params={{ webinarId }}>
                    {t('Button.BackToPoll')}
                </BackLink>
                <ContentHeader testId="pollList" contentHeader={t('Content.PollWeighting.PageHeading')} />
                <Heading variant="h2" classNames="mt-12 mb-6">
                    {t('Content.PollWeighting.Heading.DefaultWeight')}
                </Heading>
                <div className="max-w-4xl">
                    <p className="mb-6">{t('Content.PollWeighting.Text.DefaultWeight')}</p>
                    <Input
                        type="number"
                        label={t('Content.PollWeighting.Input.DefaultWeight')}
                        value={defaultWeight}
                        onChange={(event) => setDefaultWeight(+event.target.value)}
                        min={0}
                        classNames="max-w-xs"
                    />
                    <Heading variant="h2" classNames="mt-12 mb-6">
                        {t('Content.PollWeighting.Heading.ImportList')}
                    </Heading>
                    <p className="mb-6">
                        <Trans i18nKey="Content.PollWeighting.Text.ImportList" />
                    </p>
                    <div
                        {...getRootProps()}
                        className={`group relative w-full min-h-64 ${isDragActive && 'bg-accent-lighter'} hover:bg-accent-lighter focus-visible:bg-accent-lighter border-2 border-dashed ${!isDragActive && 'border-gray-500'} ${isDragActive && 'border-accent'} hover:border-accent focus-visible:border-accent rounded-lg flex mb-4 md:mb-6 outline-hidden overflow-hidden p-6 transition-colors duration-150`}
                    >
                        <div className="flex flex-col items-center m-auto">
                            <Upload classNames="size-12 mb-1 stroke-current group-hover:stroke-primary transition-color duration-200 ease-in-out" />
                            <p className="font-accent font-w-heading text-3xl mb-2">{isDragActive ? t('Generic.DragDrop.Active') : t('Generic.DragDrop')}</p>
                            <p>
                                <Trans components={{ span: <span className="border-b-2 border-primary" /> }} i18nKey="Generic.DragDrop.BrowseFiles" />
                            </p>
                            <p className="mt-4">
                                {t('Generic.DragDrop.AcceptedText')}
                                <span className="font-medium">{t('Content.PollWeighting.DragDrop.AcceptedFileTypes')}</span>
                            </p>
                            {rejectionCode && <ErrorMessage errorSubject="Generic.FileRejection." errorCode={rejectionCode} classNames="mt-4" />}
                        </div>
                        <input {...getInputProps()} />
                    </div>
                    {pollWeights.weights.length === 0 && <p className="mt-6">{t('Content.PollWeighting.NoWeights')}</p>}
                    {pollWeights.weights.length > 0 && (
                        <>
                            <Heading variant="h2" classNames="mt-12 mb-6">
                                {t('Content.PollWeighting.Heading.List')}
                            </Heading>
                            <div className="table-responsive bg-white rounded-lg mb-4">
                                <table className="table table-striped [&_th:nth-child(2)]:text-right [&_td:nth-child(2)]:text-right">
                                    <thead>
                                        <tr>
                                            <th>{t('Content.PollWeighting.Table.Heading.Email')}</th>
                                            <th>{t('Content.PollWeighting.Table.Heading.Factor')}</th>
                                            <th className="w-10"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pollWeights.weights.map((weight) => (
                                            <tr key={weight.emailAddress}>
                                                <td>{weight.emailAddress}</td>
                                                <td>{weight.weight}</td>
                                                <td>
                                                    <Button
                                                        type="button"
                                                        iconbtn
                                                        classNames="-my-3 translate-y-0.75"
                                                        onClick={() => removeVoteWeight(weight.emailAddress)}
                                                        isLoading={isPending}
                                                    >
                                                        <Close classNames="size-6 stroke-warning" />
                                                        <span className="sr-only">
                                                            <Trans
                                                                values={{ email: weight.emailAddress }}
                                                                i18nKey="Content.PollWeighting.Table.Button.DeleteEntry"
                                                            />
                                                        </span>
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <Button onClick={clearVoteWeights} variant="warning-outline" isLoading={isPending}>
                                {t('Content.PollWeighting.Button.ClearList')}
                            </Button>
                        </>
                    )}
                </div>
            </ContainerWebinarInner>
        </ContainerNoTopSpace>
    );
};
